import React from 'react';
import { cn } from '../../../lib/utils';

interface ThemeCardProps extends React.HTMLAttributes<HTMLDivElement> {
    variant?: 'default' | 'elevated' | 'outlined' | 'muted' | 'accent';
    children: React.ReactNode;
}

const ThemeCard = React.forwardRef<HTMLDivElement, ThemeCardProps>(
    ({ className, variant = 'default', children, ...props }, ref) => {
        const baseClasses = "rounded-lg border transition-all duration-200";

        const variants = {
            default: "bg-[hsl(var(--card-bg))] border-[hsl(var(--border))] text-[hsl(var(--card-fg))] hover:border-[hsl(var(--primary))]",
            elevated: "bg-[hsl(var(--card-bg))] border-[hsl(var(--border))] text-[hsl(var(--card-fg))] shadow-lg hover:shadow-xl border-[hsl(var(--primary))] hover:border-[hsl(var(--primary))]",
            outlined: "bg-[hsl(var(--muted))] border-2 border-[hsl(var(--primary))] text-[hsl(var(--card-fg))] hover:border-[hsl(var(--accent))]",
            muted: "bg-[hsl(var(--muted))] border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--accent))]",
            accent: "bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--primary))]"
        };

        return (
            <div
                ref={ref}
                className={cn(baseClasses, variants[variant], className)}
                {...props}
            >
                {children}
            </div>
        );
    }
);

ThemeCard.displayName = "ThemeCard";

const ThemeCardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn("flex flex-col space-y-1.5 p-6", className)}
            {...props}
        />
    )
);
ThemeCardHeader.displayName = "ThemeCardHeader";

const ThemeCardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
    ({ className, ...props }, ref) => (
        <h3
            ref={ref}
            className={cn("text-2xl font-semibold leading-none tracking-tight text-[hsl(var(--card-fg))]", className)}
            {...props}
        />
    )
);
ThemeCardTitle.displayName = "ThemeCardTitle";

const ThemeCardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
    ({ className, ...props }, ref) => (
        <p
            ref={ref}
            className={cn("text-sm text-[hsl(var(--muted-foreground))]", className)}
            {...props}
        />
    )
);
ThemeCardDescription.displayName = "ThemeCardDescription";

const ThemeCardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
    )
);
ThemeCardContent.displayName = "ThemeCardContent";

const ThemeCardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
    )
);
ThemeCardFooter.displayName = "ThemeCardFooter";

export {
    ThemeCard,
    ThemeCardHeader,
    ThemeCardTitle,
    ThemeCardDescription,
    ThemeCardContent,
    ThemeCardFooter
};






























